import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useTropicalDemoKind } from 'utils/featureFlags';
import {
  JAPAN_DIVISIONS,
  JAPAN_DIVISIONS_LAYER_DESCRIPTION,
  JAPAN_DIVISIONS_LAYER_NAME,
  JAPAN_INCIDENT_NAME,
  JAPAN_INCIDENT_TYPE,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/japanDivisions';
import {
  JAPAN_ASSETS_LAYER_TITLE,
  JAPAN_INTENSITY_LAYER_TITLE,
  JAPAN_INTENSITY_POLYGON,
  JAPAN_PLUME_LAYER_TITLE,
  JAPAN_PLUME_POLYGONS,
  JAPAN_PRIORITY_BAND_COLORS,
  rankJapanAssets,
  type JapanPolygon,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/japanEarthquakeNotification';
import type { CreateIcsIncidentLocationBody } from 'hooks/icsLocation/icsIncidentLocation.types';
import {
  keys as incidentKeys,
  type Incident,
} from 'components/IAP/hooks/incidentHooks';
import {
  findJapanChemicalOrgEvent,
  findJapanEarthquakeOrgEvent,
  orgEventKeys,
  useOrgEvents,
} from 'hooks/orgEvents/orgEventHooks';
import { internalLayersKeys } from 'hooks/internalLayer/queryKeys';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';
import type {
  CreateInternalLayerBody,
  InternalLayer,
  ListInternalLayersFilter,
  ListLayersResponse,
} from 'hooks/internalLayer/types';
import { buildSearchQueryParams } from 'hooks/shared/buildSearchQueryParams';

const ensureLocks = new Map<string, Promise<unknown>>();

async function findExistingIncident(
  groupGuid: string
): Promise<Incident | undefined> {
  const resp = await axios.get<Incident[]>(
    `${config.apiGateway.ics_incidents}/${groupGuid}`
  );
  return (resp.data ?? []).find(
    (incident) => incident.name === JAPAN_INCIDENT_NAME
  );
}

async function ensureIncident({
  groupGuid,
  userGuid,
}: {
  groupGuid: string;
  userGuid: string;
}): Promise<Incident | undefined> {
  const existing = await findExistingIncident(groupGuid);
  if (existing) return existing;

  try {
    const created = await axios.post<Incident>(
      config.apiGateway.createIncident,
      JSON.stringify({
        user_guid: userGuid,
        name: JAPAN_INCIDENT_NAME,
        group_guid: groupGuid,
        incident_types: [JAPAN_INCIDENT_TYPE],
        areas_of_responsibility: [],
        activated_users: [],
      })
    );
    return created.data;
  } catch (error) {
    // Another tab or an earlier run may have created it between our GET and
    // POST; the backend rejects the duplicate name, so re-read instead.
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return findExistingIncident(groupGuid);
    }
    throw error;
  }
}

async function linkOrgEvents({
  incident,
  orgEventIds,
}: {
  incident: Incident;
  orgEventIds: string[];
}): Promise<boolean> {
  const linked = incident.related_event_ids ?? [];
  if (orgEventIds.every((id) => linked.includes(id))) return false;

  // The PUT replaces the incident's links, so keep any added by hand.
  await axios.put(
    `${config.apiGateway.org_events}/incident/${incident.id}/related`,
    { eventIds: [...new Set([...linked, ...orgEventIds])] }
  );
  return true;
}

function polygonLocation(polygon: JapanPolygon): CreateIcsIncidentLocationBody {
  return {
    name: polygon.name,
    description: polygon.description,
    resourceType: polygon.resourceType,
    geometry: { type: 'Polygon', coordinates: [polygon.ring] },
    properties: {
      polygonColor: polygon.polygonColor,
      polygonOpacity: polygon.polygonOpacity,
    },
  };
}

const DIVISION_LOCATIONS: CreateIcsIncidentLocationBody[] = JAPAN_DIVISIONS.map(
  (division) => ({
    name: `${division.name} / ${division.nameJa}`,
    description: division.description,
    resourceType: 'customPolygon' as const,
    geometry: division.geometry,
    properties: {
      polygonColor: division.polygonColor,
      polygonOpacity: division.polygonOpacity,
    },
  })
);

/**
 * Hospitals and the release source keep their own icons; every other site is a
 * marker coloured by its priority band, so the map reads without callouts.
 */
const ASSET_LOCATIONS: CreateIcsIncidentLocationBody[] = rankJapanAssets().map(
  ({ rank, score, band, facility }) => ({
    name: `${rank}. ${facility.name} / ${facility.nameJa}`,
    description: facility.risk,
    resourceType:
      facility.symbology === 'facility' ? 'customPoint' : facility.symbology,
    geometry: { type: 'Point', coordinates: [facility.lon, facility.lat] },
    properties: {
      pointColor: JAPAN_PRIORITY_BAND_COLORS[band],
      pointOpacity: 1,
      customFields: [
        { label: 'Priority rank', value: String(rank) },
        { label: 'Priority score', value: String(score) },
        {
          label: 'Criticality tier',
          value: `Tier ${facility.criticalityTier}`,
        },
        { label: 'Impact', value: facility.impact },
        { label: 'Status', value: facility.operationalStatus },
        { label: 'Country', value: facility.country },
      ],
    },
  })
);

type IncidentLayerSpec = {
  name: string;
  description: string;
  locations: CreateIcsIncidentLocationBody[];
};

export const JAPAN_INCIDENT_LAYER_NAMES = [
  JAPAN_INTENSITY_LAYER_TITLE,
  JAPAN_PLUME_LAYER_TITLE,
  JAPAN_DIVISIONS_LAYER_NAME,
  JAPAN_ASSETS_LAYER_TITLE,
];

const JAPAN_INCIDENT_LAYERS: IncidentLayerSpec[] = [
  {
    name: JAPAN_INTENSITY_LAYER_TITLE,
    description:
      'Area of JMA seismic intensity upper 6 and above (synthetic, demo only).',
    locations: [polygonLocation(JAPAN_INTENSITY_POLYGON)],
  },
  {
    name: JAPAN_PLUME_LAYER_TITLE,
    description:
      'Modelled chlorine and ammonia plume and the Kawasaki port exclusion zone (synthetic, demo only).',
    locations: JAPAN_PLUME_POLYGONS.map(polygonLocation),
  },
  {
    name: JAPAN_DIVISIONS_LAYER_NAME,
    description: JAPAN_DIVISIONS_LAYER_DESCRIPTION,
    locations: DIVISION_LOCATIONS,
  },
  {
    name: JAPAN_ASSETS_LAYER_TITLE,
    description:
      'Defense, transport and medical assets, numbered by priority (criticality × impact).',
    locations: ASSET_LOCATIONS,
  },
];

async function ensureIncidentLayers({
  groupGuid,
  incidentId,
}: {
  groupGuid: string;
  incidentId: string;
}): Promise<boolean> {
  const resp = await axios.get<ListLayersResponse<InternalLayer>>(
    config.apiGateway.internal_layers,
    {
      params: buildSearchQueryParams<ListInternalLayersFilter>({
        filter: { groupGuid, incidentId },
      }),
    }
  );
  const byName = new Map(
    (resp.data?.data ?? []).map((layer) => [layer.name, layer])
  );

  let changed = false;
  for (const spec of JAPAN_INCIDENT_LAYERS) {
    const existing = byName.get(spec.name);
    if (existing) {
      if (existing.defaultVisible !== true) {
        await axios.patch(
          `${config.apiGateway.internal_layers}/${existing.id}`,
          {
            defaultVisible: true,
          }
        );
        changed = true;
      }
      continue;
    }

    const body: CreateInternalLayerBody = {
      groupGuid,
      incidentId,
      name: spec.name,
      description: spec.description,
      defaultVisible: true,
      icsIncidentLocations: spec.locations,
    };
    await axios.post<InternalLayer>(config.apiGateway.internal_layers, body);
    changed = true;
  }

  return changed;
}

async function ensureJapanIncidentForGroup({
  groupGuid,
  userGuid,
  orgEventIds,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  orgEventIds: string[];
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<boolean> {
  const incident = await ensureIncident({ groupGuid, userGuid });
  if (!incident) return false;

  const bothEvents = orgEventIds.length === 2;
  const linked = bothEvents && (await linkOrgEvents({ incident, orgEventIds }));
  await queryClient.invalidateQueries({
    queryKey: incidentKeys.all(groupGuid),
  });
  if (linked) {
    await queryClient.invalidateQueries({
      queryKey: orgEventKeys.incidentRelated(incident.id),
    });
  }

  const layersChanged = await ensureIncidentLayers({
    groupGuid,
    incidentId: incident.id,
  });
  if (layersChanged) {
    await queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() });
    await queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() });
  }

  return bothEvents;
}

/**
 * Seeds the Tokyo Bay incident workspace and its four internal layers:
 * intensity, plume and exclusion zone, Divisions A–E, and the ranked assets.
 *
 * Incidents carry no demo_key, so the incident name is the idempotency handle
 * and each layer is matched by name within that incident. The earthquake and
 * chemical release events come from the query, so the incident links to both as
 * soon as the sibling hook creates them.
 */
export function useEnsureJapanIncident() {
  const japanEnabled = useTropicalDemoKind() === 'japan';
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);
  const orgEventsQuery = useOrgEvents({ enabled: japanEnabled });
  const quakeEventId = findJapanEarthquakeOrgEvent(orgEventsQuery.data)?.id;
  const chemEventId = findJapanChemicalOrgEvent(orgEventsQuery.data)?.id;

  useEffect(() => {
    if (!japanEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `japan-incident:${groupGuid}`;
    let cancelled = false;

    const run = async (): Promise<void> => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        // The in-flight run may have started before both events existed.
        await existing.catch(() => undefined);
        if (!cancelled) return run();
        return;
      }

      const promise = ensureJapanIncidentForGroup({
        groupGuid,
        userGuid,
        orgEventIds: [quakeEventId, chemEventId].filter(
          (id): id is string => !!id
        ),
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        const complete = await promise;
        if (!cancelled && complete) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure Japan Tokyo Bay incident', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    japanEnabled,
    groupGuid,
    userGuid,
    quakeEventId,
    chemEventId,
    queryClient,
  ]);
}
